'use client';

import { type ComponentProps, forwardRef } from 'react';
import { SearchField } from '@frameio/vapor';
import TEXT from './generated/TEXT/SearchField';

TEXT.DEFINE({
  /** Placeholder text for the search input */
  placeholder: 'Search',
  /** Accessible label for the search input clear button */
  clearButtonAriaLabel: 'Clear input value',
  /** Accessible label shown while search is in progress */
  pendingStateLabel: 'Searching',
});

/**
 * Represents the original props from the `@frameio/vapor` `SearchField` component.
 */
type BaseProps = ComponentProps<typeof SearchField>;

/**
 * A union of prop names that should be optional on the public-facing component
 * because they are defaulted from `TEXT`.
 */
type OptionalProps = Extract<
  keyof BaseProps,
  'placeholder' | 'clearButtonAriaLabel' | 'pendingStateLabel'
>;

/**
 * Props for the `SearchFieldWithI18n` component.
 * It makes all props with default i18n values optional.
 */
export type SearchFieldWithI18nProps = Omit<BaseProps, OptionalProps> &
  Partial<Pick<BaseProps, OptionalProps>>;

/**
 * A wrapper around `@frameio/vapor`'s `SearchField` component that provides
 * default internationalization (i18n) properties.
 *
 * This component simplifies using `SearchField` by pre-configuring it with
 * standard text labels and placeholders, making those props optional.
 *
 * @remarks
 * The defaults are read inline as `TEXT.xxx` so each render resolves against
 * the current intl locale. Do not hoist these reads into a module-level
 * object/const — `TEXT` exposes live getters and any value captured at
 * module-evaluation time becomes a stale string snapshot until the next
 * hard refresh.
 *
 * @param props The props for the `SearchField` component. Any defaulted
 *   i18n props will be overridden if provided.
 * @returns A `SearchField` component with i18n defaults.
 */
export const SearchFieldWithI18n = forwardRef<
  unknown,
  SearchFieldWithI18nProps
>(function SearchFieldWithI18n(props, ref) {
  // Build the merged props inside the render so each access to `TEXT.xxx`
  // hits the live getter and resolves against the current intl locale.
  // The cast to `BaseProps` mirrors the original wrapper and reconciles the
  // partial caller-side props (which TS sees as possibly `undefined`) with
  // the base component's required string slots.
  const mergedProps = {
    placeholder: TEXT.placeholder,
    clearButtonAriaLabel: TEXT.clearButtonAriaLabel,
    pendingStateLabel: TEXT.pendingStateLabel,
    ...props,
  } as BaseProps;

  return <SearchField ref={ref} {...mergedProps} />;
});
